import { Box, Grid } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider, FieldValues } from 'react-hook-form';
import { Cell } from 'react-table';
import {
  deleteAccessControl,
  getAllPrismAccessedUsers,
  IPrismAccessControl,
  saveAccessControl,
} from 'wisestep-api';
import { WsButton, WsColors, WsDataTable, WsListingLoader } from 'wisestep-base';

import { WsWorkspaceUserFilter } from '../../components/filters/components';
import NoDataFoundCard from '../../components/noDataFoundCard';
import { SettingsHeader } from '../../components/SettingsHeader';
import SettingsIndex from '../../components/SettingsIndex';
import { WsBreadCrumbs } from '../../components/WsBreadCrumbs';
import { useWorkspaceContext } from '../../context';
import { AtsCustomAPIAccess, useAtsCustomAPIAccess } from '../../hooks/useAtsCustomAPIAccess';
import { failed, success, warning } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../teams/helper/defaultValues';

const PrismUserManagement = () => {
  const methods = useForm();
  const { handleSubmit, setValue } = methods;
  const workspaceContext = useWorkspaceContext();
  const [users, setUsers] = useState<IPrismAccessControl[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const { access: atsCustomAccessControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.MANAGE_PRISM_SETTINGS,
  ]);

  const manageAccess = workspaceContext.owns || atsCustomAccessControl[0];

  const fetchAccessControls = useCallback(async () => {
    try {
      setLoader(true);
      const response = await getAllPrismAccessedUsers();
      setUsers(response.data);
    } catch {
      setNotification(failed('Failed to get Prism Access Control List'));
    }
    setLoader(false);
  }, [setNotification]);

  useEffect(() => {
    fetchAccessControls();
  }, [fetchAccessControls]);

  const columns = [
    {
      Header: 'Email Address',
      accessor: 'email',
    },
    {
      Header: 'Role',
      accessor: 'role',
    },
  ];

  const data = useMemo(
    () =>
      users?.map((user) => ({
        email: user.userId,
        role: user.role,
      })),
    [users]
  );

  const revokeAccess = async (index: string) => {
    try {
      await deleteAccessControl(users[index].id);
      setNotification(success('Access Revoked'));
      fetchAccessControls();
    } catch {
      setNotification(failed('Failed to revoke Access, Please try again !!'));
    }
  };

  const onSubmit = async (data: FieldValues) => {
    const userEmail = data?.users?.match(/\((.*?)\)/)?.[1] ?? '';
    if (userEmail) {
      try {
        const payLoad: IPrismAccessControl = {
          userId: userEmail,
          id: 0,
          role: '',
          workspaceId: '',
        };
        setValue('users', '');
        await saveAccessControl(payLoad);
        fetchAccessControls();
        setNotification(success('Saved Access Control successfully !!'));
      } catch (error: any) {
        if (error.response.status === 400) setNotification(failed('User already exists !!'));
        else setNotification(failed('Error Saving Access Control, Please try again !!'));
      }
    } else {
      setNotification(warning('Please select at least one user'));
    }
  };
  return (
    <Box>
      <WsBreadCrumbs
        breadCrumbsList={getSettingBreadCrumbList(
          ISettingBreadCrumbTypes.APPS_AND_INTEGRATION,
          Pages['PRISM_SETTINGS']
        )}
      />
      <SettingsHeader title="Prism Settings" />
      <Grid container spacing={2}>
        <Grid item xs={3} mt={2} borderRadius={1.5}>
          <SettingsIndex
            options={[
              {
                label: 'Prism Extension Settings',
                url: '/prism-extension-settings/',
              },
              {
                label: 'User Management',
                url: '/prism-user-management/',
              },
              {
                label: 'Session Management',
                url: '/prism-session-management/',
              },
            ]}
          />
        </Grid>
        <FormProvider {...methods}>
          <Grid item container xs={9} component="form" onSubmit={handleSubmit(onSubmit)}>
            <Grid item xs={9} bgcolor={WsColors.White}>
              {!loader && <WsWorkspaceUserFilter name="users" multiple={false} key="users" />}
            </Grid>
            {manageAccess && (
              <Grid item xs={3} display="flex" justifyContent="flex-end" alignItems="center">
                <WsButton type="submit">Add as Session Creator</WsButton>
              </Grid>
            )}
            <Grid item xs={12} mt={2}>
              {users?.length > 0 ? (
                <WsDataTable
                  columns={columns}
                  data={data}
                  boldItems={['email']}
                  rowActions={[
                    {
                      label: 'Revoke Access',
                      callback: (cell: Cell[]) => revokeAccess(cell[0]?.row?.id ?? ''),
                    },
                  ]}
                />
              ) : loader ? (
                <WsListingLoader />
              ) : (
                <NoDataFoundCard text="No Users Found" />
              )}
            </Grid>
          </Grid>
        </FormProvider>
      </Grid>
    </Box>
  );
};

export default PrismUserManagement;
